/**
 * Haptics — unified Taptic Engine / Vibration wrapper.
 *
 * Background: the designer specifically called out the difference between
 * `navigator.vibrate` (a hard buzz on Android, completely ignored on iOS
 * Safari) and the native Taptic Engine (subtle, precise, premium). The
 * answer is @capacitor/haptics, which exposes a unified API that:
 *
 *   - On iOS (Capacitor shell)     → routes to UIImpactFeedbackGenerator
 *   - On Android (Capacitor shell) → routes to HapticFeedbackConstants
 *   - On web                        → HARD NO-OP (we never want to call
 *                                     navigator.vibrate from a browser tab —
 *                                     desktop Chrome on Android would buzz
 *                                     the device on every merge/cascade/drag
 *                                     and feel utterly broken)
 *
 * This wrapper adds four things on top of the raw plugin:
 *   1. Native-platform gate via Capacitor.isNativePlatform() — web is a no-op.
 *   2. All methods are fire-and-forget (never await, never throw)
 *      — haptics are cosmetic; a promise rejection must never break gameplay.
 *   3. A debounce per kind so we don't buzz 30× during a long cascade chain.
 *   4. A master `setEnabled` switch the Settings scene can toggle.
 */

import { Capacitor } from '@capacitor/core';
import { Haptics as CapHaptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

// Resolve once at module load — Capacitor's platform never changes mid-session.
const IS_NATIVE = Capacitor.isNativePlatform();

type HapticKind =
  | 'selection'   // Lightest tick — menu navigation, drag starts
  | 'light'       // Merge success
  | 'medium'      // Cascade step
  | 'heavy'       // Overcharge ready / splinter fire
  | 'success'     // Discovery, family unlock
  | 'warning'     // Decay warning ring
  | 'error';      // Corruption hit, invalid action

const DEBOUNCE_MS: Record<HapticKind, number> = {
  selection: 40,
  light: 60,
  medium: 80,
  heavy: 120,
  success: 150,
  warning: 200,
  error: 200,
};

class HapticsService {
  private enabled = true;
  private lastFired: Record<HapticKind, number> = {
    selection: 0, light: 0, medium: 0, heavy: 0, success: 0, warning: 0, error: 0,
  };

  /** Master switch (Settings scene). */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /** Lightest tick — use for menu hover, draft card snap. */
  selection(): void { this.trigger('selection'); }

  /** Merge success, single-element interaction. */
  light(): void { this.trigger('light'); }

  /** Cascade step, mid-tier merge. */
  medium(): void { this.trigger('medium'); }

  /** Splinter fire, overcharge hitting ready state, high-tier merge. */
  heavy(): void { this.trigger('heavy'); }

  /** Discovery, family unlock, achievement. */
  success(): void { this.trigger('success'); }

  /** Decay warning ring appears. */
  warning(): void { this.trigger('warning'); }

  /** Corruption hit, invalid tap. */
  error(): void { this.trigger('error'); }

  private trigger(kind: HapticKind): void {
    if (!this.enabled) return;
    // Hard no-op on web. Capacitor's web shim forwards to navigator.vibrate,
    // which causes Android Chrome (and any other Vibration-API-enabled browser)
    // to buzz on every merge/cascade/drag/menu-tap. Haptics are a native-only
    // affordance for us; the browser build stays silent.
    if (!IS_NATIVE) return;

    const now = Date.now();
    if (now - this.lastFired[kind] < DEBOUNCE_MS[kind]) return;
    this.lastFired[kind] = now;

    // Fire and forget — never await or .catch().throw() up to callers.
    // Capacitor Haptics gracefully no-ops on unsupported platforms.
    switch (kind) {
      case 'selection':
        CapHaptics.selectionStart().catch(() => {});
        break;
      case 'light':
        CapHaptics.impact({ style: ImpactStyle.Light }).catch(() => {});
        break;
      case 'medium':
        CapHaptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
        break;
      case 'heavy':
        CapHaptics.impact({ style: ImpactStyle.Heavy }).catch(() => {});
        break;
      case 'success':
        CapHaptics.notification({ type: NotificationType.Success }).catch(() => {});
        break;
      case 'warning':
        CapHaptics.notification({ type: NotificationType.Warning }).catch(() => {});
        break;
      case 'error':
        CapHaptics.notification({ type: NotificationType.Error }).catch(() => {});
        break;
    }
  }
}

/** Singleton — import { Haptics } from '../ui/Haptics' and call Haptics.heavy() etc. */
export const Haptics = new HapticsService();
